import { useEffect, useState } from 'react';
import { Package } from 'lucide-react';
import ItemImagePreviewModal from './ItemImagePreviewModal';
import { getAuthenticatedImageObjectUrl } from '../utils/authenticatedImageCache';

export interface ItemThumbnailProps {
  imageUrl?: string | null;
  alt: string;
  size?: 'sm' | 'md' | 'lg';
  previewable?: boolean;
}

const SIZE_CLASSES = {
  sm: 'h-11 w-11',
  md: 'h-16 w-16',
  lg: 'h-[100px] w-[100px]',
} as const;

const ICON_CLASSES = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
} as const;

const ItemThumbnail = ({
  imageUrl,
  alt,
  size = 'sm',
  previewable = true,
}: ItemThumbnailProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!imageUrl) {
      setPreviewUrl(null);
      setLoading(false);
      setFailed(false);
      setShowPreview(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setPreviewUrl(null);
    setShowPreview(false);

    getAuthenticatedImageObjectUrl(imageUrl)
      .then((url) => {
        if (!cancelled) {
          setPreviewUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const boxClass = `${SIZE_CLASSES[size]} rounded-lg shrink-0 overflow-hidden`;

  if (!imageUrl || failed) {
    return (
      <div className={`${boxClass} bg-slate-100 flex items-center justify-center`}>
        <Package className={`${ICON_CLASSES[size]} text-slate-700`} aria-hidden />
      </div>
    );
  }

  if (loading || !previewUrl) {
    return <div className={`${boxClass} bg-slate-100 animate-pulse`} aria-hidden />;
  }

  const image = (
    <img src={previewUrl} alt={alt} className="h-full w-full object-cover bg-slate-100" />
  );

  if (!previewable) {
    return <div className={boxClass}>{image}</div>;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowPreview(true)}
        className={`${boxClass} block p-0 border-0 bg-slate-100 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-slate-800 focus:ring-offset-2`}
        aria-label={`View ${alt} image`}
      >
        {image}
      </button>
      {showPreview && (
        <ItemImagePreviewModal
          imageUrl={previewUrl}
          alt={alt}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
};

export default ItemThumbnail;
